import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Ticket, Plus, ChevronRight, Send, Clock, CheckCircle, XCircle,
  AlertTriangle, MessageCircle, Filter, RefreshCw, Lock, User, Star,
  Edit2, Trash2, X, ArrowLeft, Paperclip, Code2, BarChart3
} from 'lucide-react';

const PRIORITY_COLORS = { critical:'var(--red)', high:'var(--orange)', medium:'var(--yellow)', low:'var(--green)' };
const STATUS_LABELS = { open:'Open', in_progress:'In Progress', waiting_on_client:'Waiting on Client', resolved:'Resolved', closed:'Closed' };
const CATEGORY_LABELS = { bug:'Bug', question:'Question', feature_request:'Feature Request', auth_issue:'Auth Issue', response_error:'Response Error', other:'Other' };

const T = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderBottom: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    flexShrink: 0,
    gap: 12,
    flexWrap: 'wrap'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10
  },
  headerRight: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap'
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    background: 'none',
    border: 'none',
    color: 'var(--text-3)',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: 'var(--font)'
  },
  title: {
    fontSize: 17,
    fontWeight: 700
  },
  filter: {
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-light)',
    borderRadius: 7,
    color: 'var(--text-2)',
    fontSize: 12,
    padding: '5px 9px',
    cursor: 'pointer'
  },
  detailActions: {
    display: 'flex',
    gap: 6
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))',
    gap: 12,
    padding: 20
  },
  statCard: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: 16,
    textAlign: 'center'
  },
  list: {
    flex: 1,
    overflow: 'auto'
  },
  centered: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40
  },
  ticketRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    borderBottom: '1px solid var(--border)',
    cursor: 'pointer',
    transition: 'background .12s',
    gap: 12
  },
  rowLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
    minWidth: 0
  },
  prioDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginTop: 5,
    flexShrink: 0
  },
  rowMain: {
    flex: 1,
    minWidth: 0
  },
  rowTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 5,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  rowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap'
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    fontSize: 11,
    color: 'var(--text-3)'
  },
  rowRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0
  },
  agentChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 11,
    color: 'var(--text-2)'
  },
  detailRoot: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  detailBody: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden'
  },
  ticketMeta: {
    width: 220,
    borderRight: '1px solid var(--border)',
    padding: 16,
    overflow: 'auto',
    flexShrink: 0
  },
  metaRow: {
    marginBottom: 12
  },
  metaLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--text-3)',
    textTransform: 'uppercase',
    letterSpacing: '.5px',
    display: 'block',
    marginBottom: 4
  },
  statusBtn: {
    fontSize: 11,
    padding: '3px 9px',
    borderRadius: 99,
    border: '1px solid var(--border)',
    background: 'none',
    color: 'var(--text-2)',
    cursor: 'pointer',
    fontFamily: 'var(--font)',
    transition: 'all .12s'
  },
  conversation: {
    flex: 1,
    overflow: 'auto',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  bubble: {
    borderRadius: 10,
    padding: '12px 14px'
  },
  bubbleClient: {
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border)'
  },
  bubbleAgent: {
    background: 'rgba(79,156,249,.07)',
    border: '1px solid rgba(79,156,249,.2)'
  },
  bubbleInternal: {
    background: 'rgba(251,191,36,.06)',
    border: '1px dashed rgba(251,191,36,.3)'
  },
  bubbleHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    marginBottom: 8
  },
  bubbleAuthor: {
    fontSize: 13,
    fontWeight: 600
  },
  bubbleTime: {
    marginLeft: 'auto',
    fontSize: 11,
    color: 'var(--text-3)'
  },
  bubbleBody: {
    fontSize: 13,
    color: 'var(--text-1)',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap'
  },
  internalTag: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    fontSize: 10,
    color: 'var(--yellow)',
    background: 'rgba(251,191,36,.12)',
    padding: '1px 6px',
    borderRadius: 4
  },
  attachedResponse: {
    marginTop: 10,
    background: 'var(--bg-base)',
    border: '1px solid var(--border)',
    borderRadius: 7,
    padding: '10px 12px'
  },
  attachedLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 11,
    color: 'var(--text-2)',
    marginBottom: 6
  },
  statusPill: {
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 700,
    fontFamily: 'var(--mono)'
  },
  codeSnip: {
    background: 'var(--bg-base)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: '10px 12px',
    fontSize: 11,
    color: '#86efac',
    overflow: 'auto',
    maxHeight: 200,
    whiteSpace: 'pre-wrap',
    lineHeight: 1.6
  },
  event: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 0'
  },
  eventLine: {
    width: 2,
    height: 14,
    background: 'var(--border)',
    borderRadius: 1,
    flexShrink: 0,
    marginLeft: 8
  },
  eventText: {
    fontSize: 11,
    color: 'var(--text-3)'
  },
  replyBox: {
    borderTop: '1px solid var(--border)',
    padding: '14px 20px',
    flexShrink: 0,
    background: 'var(--bg-surface)'
  },
  replyToggle: {
    display: 'flex',
    gap: 4,
    marginBottom: 10
  },
  toggleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px',
    borderRadius: 6,
    border: '1px solid var(--border)',
    background: 'none',
    color: 'var(--text-3)',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: 'var(--font)',
    transition: 'all .12s'
  },
  toggleActive: {
    background: 'var(--accent-dim)',
    borderColor: 'var(--accent)',
    color: 'var(--accent)'
  },
  replyInput: {
    width: '100%',
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-light)',
    borderRadius: 8,
    color: 'var(--text-1)',
    fontSize: 13,
    padding: '10px 12px',
    resize: 'none',
    fontFamily: 'var(--font)',
    lineHeight: 1.5
  },
  replyFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8
  },
  attachCheck: {
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 14
  }
};

export default function TicketsPanel({ projectId, endpoints = [], environments = [], lastResponse = null }) {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [replyText, setReplyText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState('list'); // list | detail | stats
  const replyRef = useRef(null);

  const isAgent  = ['admin','agent','member'].includes(user?.role);
  const isClient = user?.role === 'client';
  const isAdmin  = ['admin','member'].includes(user?.role);

  const STAT_FILTERS = {
    Total: '',
    Open: 'open',
    'In Progress': 'in_progress',
    Resolved: 'resolved',
    Critical: 'critical'
  };

  const onStatCardClick = (label) => {
    if (label === 'Avg Resolve') return;
    setFilterStatus(STAT_FILTERS[label] || '');
    setView('list');
  };

  const [form, setForm] = useState({
    title:'', description:'', priority:'medium', category:'question',
    endpoint_id:'', assigned_agent:'', attachment: null,
    attach_response: false,
  });

  useEffect(() => { load(); loadStats(); }, [projectId, filterStatus, filterPriority]);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterPriority) params.append('priority', filterPriority);
      const r = await api.get(`/projects/${projectId}/tickets?${params}`);
      setTickets(r.data.tickets || []);
    } catch {}
    finally { setLoading(false); }
  }

  async function loadStats() {
    try {
      const r = await api.get(`/projects/${projectId}/tickets/stats`);
      setStats(r.data);
    } catch {}
  }

  async function loadDetail(id) {
    setDetailLoading(true);
    try {
      const r = await api.get(`/tickets/${id}`);
      setDetail(r.data);
      setView('detail');
    } catch { toast.error('Failed to load ticket'); }
    finally { setDetailLoading(false); }
  }

  async function createTicket(e) {
    e.preventDefault();
    try {
      const payload = new FormData();
      Object.keys(form).forEach(key => {
        if (key === 'attachment' && form[key]) {
          payload.append('attachment', form[key]);
        } else if (key !== 'attachment') {
          payload.append(key, form[key]);
        }
      });
      if (form.attach_response && lastResponse) {
        payload.append('response_status', lastResponse.status);
        payload.append('response_body', typeof lastResponse.data === 'object' ? JSON.stringify(lastResponse.data, null, 2) : String(lastResponse.data || ''));
        payload.append('response_time', lastResponse.response_time);
      }
      await api.post(`/projects/${projectId}/tickets`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Ticket created!');
      setShowCreate(false);
      setForm({ title:'', description:'', priority:'medium', category:'question', endpoint_id:'', assigned_agent:'', attachment: null, attach_response: false });
      load(); loadStats();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function updateStatus(id, status) {
    try {
      await api.put(`/tickets/${id}`, { status });
      toast.success('Status updated');
      loadDetail(id); load(); loadStats();
    } catch { toast.error('Failed'); }
  }

  async function assignAgent(id, agentId) {
    try {
      await api.put(`/tickets/${id}`, { assigned_agent: agentId || null });
      toast.success('Assigned');
      loadDetail(id);
    } catch { toast.error('Failed'); }
  }

  async function sendReply() {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      await api.post(`/tickets/${detail.id}/replies`, {
        content: replyText,
        is_internal: isInternal,
        content_type: replyText.startsWith('{') || replyText.startsWith('[') ? 'json' : 'text'
      });
      setReplyText('');
      setIsInternal(false);
      loadDetail(detail.id);
    } catch { toast.error('Failed to send reply'); }
    finally { setSendingReply(false); }
  }

  function timeAgo(ts) {
    const diff = Date.now() - new Date(ts);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs/24)}d ago`;
  }

  return (
    <div style={T.root}>
      {/* ── Header ── */}
      <div style={T.header}>
        <div style={T.headerLeft}>
          {view !== 'list' && (
            <button style={T.backBtn} onClick={() => setView('list')}>
              <ArrowLeft size={14} /> Back
            </button>
          )}
          <Ticket size={18} style={{ color:'var(--accent)' }} />
          <h2 style={T.title}>{view==='detail' ? detail?.title || 'Ticket' : view==='stats' ? 'Ticket Analytics' : isClient ? 'My Support Tickets' : 'Support Queue'}</h2>
        </div>
        <div style={T.headerRight}>
          {view === 'list' && (
            <>
              <select style={T.filter} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All Status</option>
                {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select style={T.filter} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
                <option value="">All Priority</option>
                {['critical','high','medium','low'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
              </select>
              <button className="btn btn-ghost btn-sm" onClick={() => setView(v => v==='stats' ? 'list' : 'stats')}>
                <BarChart3 size={13} /> {view==='stats' ? 'List' : 'Stats'}
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
                <Plus size={13} /> New Ticket
              </button>
            </>
          )}
          {view === 'detail' && detail && isAgent && (
            <div style={T.detailActions}>
              <select style={T.filter} value={detail.status} onChange={e => updateStatus(detail.id, e.target.value)}>
                {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          )}
        </div>
        </div>
      {view === 'stats' && stats && (
        <div style={T.statsGrid}>
          {[
            { label:'Total',       val: stats.total,       color:'var(--text-2)' },
            { label:'Open',        val: stats.open,        color:'var(--accent)' },
            { label:'In Progress', val: stats.in_progress, color:'var(--orange)' },
            { label:'Resolved',    val: stats.resolved,    color:'var(--green)' },
            { label:'Critical',    val: stats.critical,    color:'var(--red)' },
            { label:'Avg Resolve', val: stats.avg_resolution_hours ? `${Math.round(stats.avg_resolution_hours)}h` : '—', color:'var(--purple)' },
          ].map(s => (
            <div
              key={s.label}
              style={{
                ...T.statCard,
                cursor: s.label === 'Avg Resolve' ? 'default' : 'pointer',
                opacity: s.label === 'Avg Resolve' ? 1 : 0.96
              }}
              onClick={() => onStatCardClick(s.label)}
            >
              <div style={{ fontSize:28, fontWeight:800, color:s.color, lineHeight:1 }}>{s.val ?? 0}</div>
              <div style={{ fontSize:11, color:'var(--text-3)', marginTop:4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── List view ── */}
      {view === 'list' && (
        <div style={T.list}>
          {loading && <div style={T.centered}><RefreshCw size={20} style={{ animation:'spin 1s linear infinite', color:'var(--text-3)' }} /></div>}
          {!loading && !tickets.length && (
            <div className="empty-state">
              <Ticket size={44} style={{ opacity:.15 }} />
              <h3>{isClient ? 'No tickets yet' : 'No tickets in queue'}</h3>
              <p>{isClient ? 'Encountered an issue? Create a support ticket.' : 'No tickets match your filters.'}</p>
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={14}/> Create Ticket</button>
            </div>
          )}
          {tickets.map(t => (
            <div key={t.id} style={T.ticketRow} onClick={() => loadDetail(t.id)}>
              <div style={T.rowLeft}>
                <div style={{ ...T.prioDot, background:PRIORITY_COLORS[t.priority] || 'var(--text-3)' }} />
                <div style={T.rowMain}>
                  <div style={T.rowTitle}>{t.title}</div>
                  <div style={T.rowMeta}>
                    <span className={`badge badge-${t.status}`}>{STATUS_LABELS[t.status] || t.status}</span>
                    <span style={T.metaItem}><MessageCircle size={10}/> {t.reply_count || 0}</span>
                    {t.endpoint_name && <span style={T.metaItem}><Code2 size={10}/> {t.endpoint_method} {t.endpoint_name}</span>}
                    <span style={T.metaItem}>{timeAgo(t.created_at)}</span>
                  </div>
                </div>
              </div>
              <div style={T.rowRight}>
                {t.agent_name ? (
                  <div style={T.agentChip}>
                    <div className="avatar" style={{ width:18, height:18, fontSize:8 }}>{t.agent_name[0]}</div>
                    <span style={{ fontSize:11 }}>{t.agent_name}</span>
                  </div>
                ) : <span style={{ fontSize:11, color:'var(--text-3)' }}>Unassigned</span>}
                <ChevronRight size={14} style={{ color:'var(--text-3)' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Detail view ── */}
      {view === 'detail' && (
        <div style={T.detailRoot}>
          {detailLoading && <div style={T.centered}><RefreshCw size={20} style={{ animation:'spin 1s linear infinite' }} /></div>}
          {!detailLoading && detail && (
            <>
              <div style={T.detailBody}>
                {/* Ticket info */}
                <div style={T.ticketMeta}>
                  <div style={T.metaRow}>
                    <span style={T.metaLabel}>Status</span>
                    <span className={`badge badge-${detail.status}`}>{STATUS_LABELS[detail.status]}</span>
                  </div>
                  <div style={T.metaRow}>
                    <span style={T.metaLabel}>Priority</span>
                    <span style={{ color:PRIORITY_COLORS[detail.priority], fontWeight:600, fontSize:12 }}>
                      {detail.priority?.toUpperCase()}
                    </span>
                  </div>
                  <div style={T.metaRow}>
                    <span style={T.metaLabel}>Category</span>
                    <span style={{ fontSize:12 }}>{CATEGORY_LABELS[detail.category] || detail.category}</span>
                  </div>
                  <div style={T.metaRow}>
                    <span style={T.metaLabel}>Created by</span>
                    <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                      <div className="avatar" style={{ width:18, height:18, fontSize:8 }}>{detail.creator_name?.[0]}</div>
                      <span style={{ fontSize:12 }}>{detail.creator_name}</span>
                      <span className={`role-chip role-${detail.creator_role}`}>{detail.creator_role}</span>
                    </div>
                  </div>
                  <div style={T.metaRow}>
                    <span style={T.metaLabel}>Agent</span>
                    {isAdmin ? (
                      <AgentSelector ticketId={detail.id} currentAgent={detail.assigned_agent} agentName={detail.agent_name} onAssign={id => assignAgent(detail.id, id)} />
                    ) : (
                      <span style={{ fontSize:12 }}>{detail.agent_name || 'Unassigned'}</span>
                    )}
                  </div>
                  <div style={T.metaRow}>
                    <span style={T.metaLabel}>Opened</span>
                    <span style={{ fontSize:12 }}>{new Date(detail.created_at).toLocaleString()}</span>
                  </div>
                  {detail.endpoint_name && (
                    <div style={T.metaRow}>
                      <span style={T.metaLabel}>Endpoint</span>
                      <span style={{ fontSize:12, fontFamily:'var(--mono)' }}>{detail.endpoint_method} {detail.endpoint_path}</span>
                    </div>
                  )}

                  {/* Status actions */}
                  {isAgent && (
                    <div style={{ marginTop:16 }}>
                      <div style={T.metaLabel}>Quick Actions</div>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:6 }}>
                        {Object.keys(STATUS_LABELS).map(s => (
                          <button key={s} style={{ ...T.statusBtn, opacity: detail.status===s ? 1 : 0.5, borderColor: detail.status===s ? 'var(--accent)' : 'var(--border)' }}
                            onClick={() => updateStatus(detail.id, s)}>
                            {STATUS_LABELS[s]}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Conversation */}
                <div style={T.conversation}>
                  {/* Original message */}
                  <div style={{ ...T.bubble, ...T.bubbleClient }}>
                    <div style={T.bubbleHead}>
                      <div className="avatar avatar-sm">{detail.creator_name?.[0]}</div>
                      <span style={T.bubbleAuthor}>{detail.creator_name}</span>
                      <span className={`role-chip role-${detail.creator_role}`}>{detail.creator_role}</span>
                      <span style={T.bubbleTime}>{timeAgo(detail.created_at)}</span>
                    </div>
                    <div style={T.bubbleBody}>{detail.description}</div>

                    {/* Attached request/response */}
                    {detail.response_status && (
                      <div style={T.attachedResponse}>
                        <div style={T.attachedLabel}>
                          <Paperclip size={11} /> Attached API Response
                          <span style={{ ...T.statusPill, color: detail.response_status < 300 ? 'var(--green)' : 'var(--red)', background: detail.response_status < 300 ? 'rgba(52,211,153,.15)' : 'rgba(248,113,113,.15)' }}>
                            {detail.response_status}
                          </span>
                          {detail.response_time && <span style={{ fontSize:10, color:'var(--text-3)' }}>{detail.response_time}ms</span>}
                        </div>
                        {detail.request_url && <div style={{ fontSize:11, color:'var(--text-3)', fontFamily:'var(--mono)', marginBottom:6 }}>{detail.request_method} {detail.request_url}</div>}
                        {detail.response_body && (
                          <pre style={{ ...T.codeSnip, maxHeight:120 }} className="mono">
                            {(() => { try { return JSON.stringify(JSON.parse(detail.response_body),null,2); } catch { return detail.response_body; } })()}
                          </pre>
                        )}
                      </div>
                    )}

                    {/* Attachment */}
                    {detail?.attachment_path && (
                      <div style={T.attachedResponse}>
                        <div style={T.attachedLabel}>
                          <Paperclip size={11} /> Attachment
                          <a href={`http://localhost:5001/${detail.attachment_path}`} target="_blank" style={{ marginLeft:8, color:'var(--accent)' }}>Download</a>
                        </div>
                      </div>
                    )}

                  </div>

                  {/* Replies */}
                  {(detail.replies || []).map(r => (
                    <div key={r.id} style={{ ...T.bubble, ...(r.is_internal ? T.bubbleInternal : r.author_role === 'client' ? T.bubbleClient : T.bubbleAgent) }}>
                      <div style={T.bubbleHead}>
                        <div className="avatar avatar-sm">{r.author_name?.[0]}</div>
                        <span style={T.bubbleAuthor}>{r.author_name}</span>
                        <span className={`role-chip role-${r.author_role}`}>{r.author_role}</span>
                        {r.is_internal && <span style={T.internalTag}><Lock size={9}/> Internal</span>}
                        <span style={T.bubbleTime}>{timeAgo(r.created_at)}</span>
                      </div>
                      {r.content_type === 'json' ? (
                        <pre style={T.codeSnip} className="mono">{(() => { try { return JSON.stringify(JSON.parse(r.content),null,2); } catch { return r.content; } })()}</pre>
                      ) : (
                        <div style={T.bubbleBody}>{r.content}</div>
                      )}
                    </div>
                  ))}

                  {/* Events */}
                  {(detail.events || []).filter(e => e.event_type !== 'replied').map(e => (
                    <div key={e.id} style={T.event}>
                      <div style={T.eventLine} />
                      <span style={T.eventText}>
                        <strong>{e.actor_name}</strong> {e.event_type.replace(/_/g,' ')}
                        {e.old_value && e.new_value && <> <s>{e.old_value}</s> → <strong>{e.new_value}</strong></>}
                        <span style={{ color:'var(--text-3)', marginLeft:6 }}>{timeAgo(e.created_at)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reply box */}
              {detail.status !== 'closed' && (
                <div style={T.replyBox}>
                  {isAgent && (
                    <div style={T.replyToggle}>
                      <button style={{ ...T.toggleBtn, ...(isInternal ? T.toggleActive : {}) }} onClick={() => setIsInternal(true)}>
                        <Lock size={11}/> Internal Note
                      </button>
                      <button style={{ ...T.toggleBtn, ...(!isInternal ? T.toggleActive : {}) }} onClick={() => setIsInternal(false)}>
                        <Send size={11}/> Reply to Client
                      </button>
                    </div>
                  )}
                  <textarea
                    ref={replyRef}
                    style={{ ...T.replyInput, borderColor: isInternal ? 'rgba(251,191,36,.4)' : 'var(--border-light)' }}
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    placeholder={isInternal ? 'Internal note (not visible to client)...' : 'Type your reply...'}
                    rows={4}
                    onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') sendReply(); }}
                  />
                  <div style={T.replyFooter}>
                    <span style={{ fontSize:11, color:'var(--text-3)' }}>Ctrl+Enter to send</span>
                    <button className="btn btn-primary btn-sm" onClick={sendReply} disabled={sendingReply || !replyText.trim()}>
                      {sendingReply ? <RefreshCw size={12} style={{ animation:'spin 1s linear infinite' }} /> : <Send size={12}/>}
                      {isInternal ? 'Add Note' : 'Send Reply'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Create Ticket Modal ── */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
              <h3 className="modal-title" style={{ margin:0 }}>Create Support Ticket</h3>
              <button className="btn-icon" onClick={() => setShowCreate(false)}><X size={16}/></button>
            </div>
            <form onSubmit={createTicket}>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input placeholder="Briefly describe the issue" value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} required autoFocus />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select value={form.priority} onChange={e => setForm(f=>({...f,priority:e.target.value}))}>
                    {['low','medium','high','critical'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select value={form.category} onChange={e => setForm(f=>({...f,category:e.target.value}))}>
                    {Object.entries(CATEGORY_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              {isAgent && (
                <div className="form-group">
                  <label className="form-label">Assign to</label>
                  <AgentSelector
                    ticketId={null}
                    currentAgent={form.assigned_agent}
                    onAssign={agentId => setForm(f => ({ ...f, assigned_agent: agentId || '' }))}
                  />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Attach File (PNG, JPEG, PDF, DOC only, max 5MB)</label>
                <input type="file" accept=".png,.jpg,.jpeg,.pdf,.doc" onChange={e => setForm(f => ({ ...f, attachment: e.target.files[0] || null }))} />
              </div>
              {endpoints.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Related Endpoint (optional)</label>
                  <select value={form.endpoint_id} onChange={e => setForm(f=>({...f,endpoint_id:e.target.value}))}>
                    <option value="">None</option>
                    {endpoints.map(ep => <option key={ep.id} value={ep.id}>{ep.method} {ep.path} — {ep.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Description *</label>
                <textarea rows={5} placeholder="Describe the issue in detail. Include steps to reproduce, expected vs actual behavior..." value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} required />
              </div>
              {lastResponse && (
                <div style={T.attachCheck}>
                  <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                    <input type="checkbox" checked={form.attach_response} onChange={e => setForm(f=>({...f,attach_response:e.target.checked}))} />
                    <span style={{ fontSize:13 }}>Attach last API response (status {lastResponse.status}, {lastResponse.response_time}ms)</span>
                  </label>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create Ticket</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function AgentSelector({ ticketId, currentAgent, onAssign }) {
  const [agents, setAgents] = useState([]);
  useEffect(() => {
    api.get('/admin/users?role=agent').then(r => setAgents(r.data)).catch(() => {});
    api.get('/admin/users?role=admin').then(r => setAgents(a => [...a, ...r.data])).catch(() => {});
  }, []);
  return (
    <select
      style={{ fontSize: 12, padding: '3px 7px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text-1)' }}
      value={currentAgent || ''}
      onChange={e => onAssign(e.target.value || null)}
    >
      <option value="">Unassigned</option>
      {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
    </select>
  );
}
